// Переговорные комнаты, сообщения, NPC-визиты, соглашения.
// Вынесено из api.ts (Фаза 1, разбор клиента по доменам).

import { request } from "./client";
import { type CycleParticipant } from "./common";
import { type CampaignCycleOverview } from "./cycle";
import { type CampaignAvatar, type MediaAsset } from "./media";

export type DiscussionRoomType =
  "PUBLIC" | "PRIVATE" | "NPC_PUBLIC" | "NPC_PRIVATE" | "NPC_GROUP";
export type DiscussionMessageSenderType = "USER" | "NPC" | "SYSTEM";
export type NpcVisitStatus =
  | "PLANNED"
  | "AVAILABLE"
  | "ACTIVE"
  | "RESOLVED"
  | "MISSED"
  | "EXPIRED"
  | "CANCELLED";
export type NpcDialogueState =
  | "INTRODUCTION"
  | "INTERESTS_CLARIFIED"
  | "OFFER_MADE"
  | "TERMS_DISCUSSION"
  | "READY_FOR_ACCEPTANCE"
  | "ACCEPTED"
  | "PARTIAL_ACCEPTED"
  | "REJECTED"
  | "EXPIRED";
export interface DiscussionMessage {
  id: string;
  senderType: DiscussionMessageSenderType;
  senderUserId?: string;
  senderCampaignMemberId?: string;
  senderDisplayName: string;
  senderAvatar?: CampaignAvatar;
  content: string;
  actionText?: string;
  isMine: boolean;
  createdAt: string;
}
export type NpcVisitTerminalTone =
  "ACCEPTED" | "PARTIAL" | "MISSED" | "NEUTRAL";
export interface NpcVisit {
  id: string;
  status: NpcVisitStatus;
  statusLabel: string;
  terminalTitle?: string;
  terminalText?: string;
  terminalConsequence?: string;
  terminalTone?: NpcVisitTerminalTone;
  characterName: string;
  characterTitle: string;
  personaText: string;
  roomType: DiscussionRoomType;
  roomId?: string;
  plannedAt: string;
  latestActivationAt: string;
  activatedAt?: string;
  expiresAt?: string;
  resolvedAt?: string;
  objectiveText: string;
  dialogueState: NpcDialogueState;
  dialogueStageLabel: string;
  offerGivesText?: string;
  offerRequestsText?: string;
  offerDurationText?: string;
  canAccept: boolean;
  playerAcceptedAt?: string;
  progressScore: number;
  successThreshold: number;
  isInvited: boolean;
  outcomeNarrative?: string;
  missedNarrative?: string;
  portraitImages: MediaAsset[];
  roomSceneImages: MediaAsset[];
}
export interface NegotiationAgreement {
  id: string;
  roomId: string;
  createdByUserId: string;
  createdByCampaignMemberId: string;
  createdByDisplayName: string;
  createdByAvatar: CampaignAvatar;
  participantUserIds: string[];
  title: string;
  terms: string;
  linkedInitiativeId?: string;
  isMine: boolean;
  createdAt: string;
}
export interface DiscussionRoom {
  id: string;
  roomType: DiscussionRoomType;
  title: string;
  npcVisitId?: string;
  roomExpiresAt?: string;
  closedAt?: string;
  members: CycleParticipant[];
  messages: DiscussionMessage[];
  agreements: NegotiationAgreement[];
  unreadCount: number;
  canPost: boolean;
  createdAt: string;
}

export const discussionApi = {
  createDiscussionRoom: (
    campaignId: string,
    title: string,
    memberUserIds: string[],
  ) =>
    request<CampaignCycleOverview>(
      `/api/campaigns/${campaignId}/cycles/current/rooms`,
      { method: "POST", body: JSON.stringify({ title, memberUserIds }) },
    ),
  sendDiscussionMessage: (
    campaignId: string,
    roomId: string,
    content: string,
  ) =>
    request<CampaignCycleOverview>(
      `/api/campaigns/${campaignId}/cycles/current/rooms/${roomId}/messages`,
      { method: "POST", body: JSON.stringify({ content }) },
    ),
  createNegotiationAgreement: (
    campaignId: string,
    roomId: string,
    payload: { title: string; terms: string; participantUserIds: string[] },
  ) =>
    request<CampaignCycleOverview>(
      `/api/campaigns/${campaignId}/cycles/current/rooms/${roomId}/agreements`,
      { method: "POST", body: JSON.stringify(payload) },
    ),
};
